import { PUB_SUB_EVENTS } from '../base/constants';
import { publish } from '../base/pubsub';

/**
 * Product Card Add to Cart functionality.
 * Handles adding products to cart from product cards.
 */
class ProductCardATB {
  constructor() {
    // Check if routes are available
    // @ts-expect-error - Custom window property
    if (!window.routes?.cart_add_url) {
      console.warn('ProductCardATB: window.routes.cart_add_url not available, will use fallback URL');
    }
    this.init();
  }

  /**
   * Initialize the product card add to cart functionality.
   */
  init() {
    this.bindEvents();
  }

  /**
   * Bind event listeners to product card add to cart buttons.
   */
  bindEvents() {
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!target) return;

      const button = /** @type {Element} */ (target).closest('.js-product-card-atb');
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        this.handleAddToCart(/** @type {HTMLElement} */ (button));
      }
    });
  }

  /**
   * Handle the add to cart button click.
   * @param {HTMLElement} button - The add to cart button element.
   */
  async handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const isAvailable = button.dataset.available === 'true';

    if (!variantId || !isAvailable) {
      return;
    }

    // Show loading state.
    this.setLoadingState(button, true);

    try {
      // Use the same configuration as the product form.
      const config = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('add', ''); // Required submit button name

      // Add sections to update.
      const sectionsToRender = this.getSectionsToRender();
      formData.append('sections', sectionsToRender.map((section) => section.section).join(','));
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      // Check if routes are available, fallback to default Shopify cart add URL if not
      // @ts-expect-error - Custom window property
      const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(cartAddUrl, config);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart add request failed:', {
          status: response.status,
          statusText: response.statusText,
          responseText: errorText
        });
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status) {
        // Handle error.
        this.handleError(button, data.description || 'Error adding product to cart');
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-card-atb',
          productVariantId: variantId,
          errors: data.errors || data.description,
          message: data.message
        });
      } else {
        // Handle success.
        this.handleSuccess(button);

        // Update cart components with section data.
        await this.updateCartComponents(data);

        // Publish cart update event after components are updated.
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-card-atb',
          productVariantId: variantId,
          cartData: data
        });

        // Dispatch custom event for gift wrap system.
        const addToCartEvent = new CustomEvent('PRODUCT_ADD_TO_CART_SUCCESS', {
          detail: {
            source: 'product-card-atb',
            variantId: variantId,
            quantity: '1',
            cartData: data
          },
          bubbles: true
        });
        document.dispatchEvent(addToCartEvent);
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
      this.handleError(button, 'Network error occurred');
    } finally {
      // Reset loading state.
      this.setLoadingState(button, false);
    }
  }

  /**
   * Get the sections that need to be rendered when cart updates.
   * @returns {Array} Array of section objects.
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.shopify-section'
      }
    ];
  }

  /**
   * Update all cart-related components with new cart data.
   * @param {Object} cartData - The updated cart data.
   */
  async updateCartComponents(cartData) {
    if (cartData.sections) {
      this.updateSectionsFromResponse(cartData);
    }

    this.updateCartCount(cartData);

    // Force update cart drawer to ensure checkout button total updates
    await this.forceUpdateCartComponents();
  }

  /**
   * Update sections from the cart response.
   * @param {Object} cartData - The updated cart data.
   */
  updateSectionsFromResponse(cartData) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace = document.getElementById(section.id);
      if (elementToReplace && cartData.sections[section.section]) {
        const sectionHtml = this.getSectionInnerHTML(cartData.sections[section.section], section.selector);
        elementToReplace.innerHTML = sectionHtml;
      }
    });
  }

  /**
   * Force update cart components by fetching fresh data.
   */
  async forceUpdateCartComponents() {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof (/** @type {any} */ (cartDrawer).updateCart) === 'function') {
        await /** @type {any} */ (cartDrawer).updateCart();
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof (/** @type {any} */ (cartItems).onCartUpdate) === 'function') {
        /** @type {any} */ (cartItems).onCartUpdate();
      }

      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof (/** @type {any} */ (cartDrawerItems).onCartUpdate) === 'function') {
        /** @type {any} */ (cartDrawerItems).onCartUpdate();
      }
    } catch (error) {
      console.error('Error forcing cart component updates:', error);
    }
  }

  /**
   * Trigger free shipping threshold update.
   * @param {Object} cartData - The updated cart data.
   */
  triggerFreeShippingUpdate(cartData) {
    const customEvent = new CustomEvent('cart-updated', {
      detail: {
        cartData: cartData,
        source: 'product-card-atb'
      }
    });
    document.dispatchEvent(customEvent);

    setTimeout(() => {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-card-atb-force',
        productVariantId: cartData.id,
        cartData: cartData
      });
    }, 100);
  }

  /**
   * Get the inner HTML from a section response.
   * @param {string} html - The HTML string.
   * @param {string} selector - The CSS selector.
   * @returns {string} The inner HTML.
   */
  getSectionInnerHTML(html, selector) {
    const parsedElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsedElement ? parsedElement.innerHTML : '';
  }

  /**
   * Update the cart count in the header.
   * @param {Object} cartData - The updated cart data.
   */
  updateCartCount(cartData) {
    const cartCountBubbleItems = document.querySelector('.cart-count-bubble .visually-hidden');
    const cartCountBubbleItemsCounter = document.querySelector('.cart-count-bubble [aria-hidden="true"]');

    if (cartCountBubbleItems) {
      const itemCount = cartData.item_count || 0;
      const [, cartCountBubbleItemsLabel] = cartCountBubbleItems.textContent?.split(' ') ?? [];
      cartCountBubbleItems.textContent = `${cartCountBubbleItemsLabel} ${itemCount}`;
    }

    if (cartCountBubbleItemsCounter) {
      const itemCount = cartData.item_count || 0;
      cartCountBubbleItemsCounter.textContent = `${itemCount}`;
    }
  }

  /**
   * Set the loading state of the button.
   * @param {HTMLElement} button - The button element.
   * @param {boolean} isLoading - Whether the button should show loading state.
   */
  setLoadingState(button, isLoading) {
    if (isLoading) {
      // Store the original text before changing to loading state
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent?.trim() || '';
      }

      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
      }
      button.textContent = 'Adding...';
      button.classList.add('is-loading');
    } else {
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
      }
      // Use the stored original text
      const originalText = button.dataset.originalText || 'Add to bag';
      button.textContent = originalText;
      button.classList.remove('is-loading');
    }
  }

  /**
   * Handle successful add to cart.
   * @param {HTMLElement} button - The button element.
   */
  handleSuccess(button) {
    button.textContent = 'Added!';
    button.classList.add('is-success');

    // Reset button text after a short delay.
    setTimeout(() => {
      const originalText = button.dataset.originalText || 'Add to bag';
      button.textContent = originalText;
      button.classList.remove('is-success');
    }, 2000);
  }

  /**
   * Handle error when adding to cart.
   * @param {HTMLElement} button - The button element.
   * @param {string} errorMessage - The error message to display.
   */
  handleError(button, errorMessage) {
    button.textContent = 'Error';
    button.classList.add('is-error');

    // Show error message in console for debugging.
    console.error('Add to cart error:', errorMessage);

    // Reset button text after a short delay.
    setTimeout(() => {
      const originalText = button.dataset.originalText || 'Add to bag';
      button.textContent = originalText;
      button.classList.remove('is-error');
    }, 3000);
  }
}

// Initialize the product card add to cart functionality.
document.addEventListener('DOMContentLoaded', () => {
  new ProductCardATB();
});

export default ProductCardATB;
